"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { isFeeEstimateMethodId, type FeeEstimateMethodId } from "@/lib/uniswap/fee-estimate-methods";
const ASSUMPTION_CONTROLS_STORAGE_KEY = "unilytics.debug.assumption-controls-enabled";
const VOL_TVL_STORAGE_KEY = "unilytics.debug.vol-tvl-enabled";
const HEADLINE_FEE_METHOD_STORAGE_KEY = "unilytics.debug.headline-fee-method";
interface DebugSettingsContextValue {
  assumptionControlsEnabled: boolean;
  setAssumptionControlsEnabled: (enabled: boolean) => void;
  volTvlEnabled: boolean;
  setVolTvlEnabled: (enabled: boolean) => void;
  headlineFeeMethodOverride: FeeEstimateMethodId | null;
  setHeadlineFeeMethodOverride: (methodId: FeeEstimateMethodId | null) => void;
}
const DebugSettingsContext = createContext<DebugSettingsContextValue | null>(null);
function readStoredHeadlineOverride(): FeeEstimateMethodId | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(HEADLINE_FEE_METHOD_STORAGE_KEY)?.trim();
  if (stored && isFeeEstimateMethodId(stored)) return stored;
  return null;
}
export function DebugSettingsProvider(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  const [assumptionControlsEnabled, setAssumptionControlsEnabledState] = useState(false);
  const [volTvlEnabled, setVolTvlEnabledState] = useState(false);
  const [headlineFeeMethodOverride, setHeadlineFeeMethodOverrideState] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof window === "undefined") {
        return;
      }
      setAssumptionControlsEnabledState(window.localStorage.getItem(ASSUMPTION_CONTROLS_STORAGE_KEY) === "true");
      setVolTvlEnabledState(window.localStorage.getItem(VOL_TVL_STORAGE_KEY) === "true");
      setHeadlineFeeMethodOverrideState(readStoredHeadlineOverride());
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = enabled => {
      setAssumptionControlsEnabledState(enabled);
      if (typeof window === "undefined") {
        return;
      }
      window.localStorage.setItem(ASSUMPTION_CONTROLS_STORAGE_KEY, String(enabled));
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setAssumptionControlsEnabled = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = enabled_0 => {
      setVolTvlEnabledState(enabled_0);
      if (typeof window === "undefined") {
        return;
      }
      window.localStorage.setItem(VOL_TVL_STORAGE_KEY, String(enabled_0));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const setVolTvlEnabled = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = methodId => {
      setHeadlineFeeMethodOverrideState(methodId);
      if (typeof window === "undefined") {
        return;
      }
      if (methodId) {
        window.localStorage.setItem(HEADLINE_FEE_METHOD_STORAGE_KEY, methodId);
      } else {
        window.localStorage.removeItem(HEADLINE_FEE_METHOD_STORAGE_KEY);
      }
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const setHeadlineFeeMethodOverride = t5;
  let t6;
  if ($[5] !== assumptionControlsEnabled || $[6] !== headlineFeeMethodOverride || $[7] !== volTvlEnabled) {
    t6 = {
      assumptionControlsEnabled,
      setAssumptionControlsEnabled,
      volTvlEnabled,
      setVolTvlEnabled,
      headlineFeeMethodOverride,
      setHeadlineFeeMethodOverride
    };
    $[5] = assumptionControlsEnabled;
    $[6] = headlineFeeMethodOverride;
    $[7] = volTvlEnabled;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const value = t6;
  let t7;
  if ($[9] !== children || $[10] !== value) {
    t7 = <DebugSettingsContext.Provider value={value}>{children}</DebugSettingsContext.Provider>;
    $[9] = children;
    $[10] = value;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export function useDebugSettings() {
  const context = useContext(DebugSettingsContext);
  if (!context) {
    throw new Error("useDebugSettings must be used within a DebugSettingsProvider");
  }
  return context;
}